import { Alert, AlertIcon, Button, ModalBody, ModalFooter, Text, useToast } from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useNavigate } from 'react-router-dom';

import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';

import { CoverageReviewTable } from './CoverageReviewTable';
import { BlockersAlert, ErrorAlert, PendingBody, ReviewShell } from './ReviewLayout';
import { testIds } from './testIds';

export function CoverageReviewModal({
  runId,
  isRetry,
  onClose,
}: {
  runId: string;
  isRetry: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const navigate = useNavigate();
  const utils = trpcAdmin.useUtils();

  const { data: coverage, error } = trpcAdmin.unitAssessment.getDraftCoverage.useQuery(
    { runId },
    { refetchOnWindowFocus: false },
  );

  const continueMutation = trpcAdmin.unitsOfCompetency.continueFromReview.useMutation({
    onSuccess: async () => {
      toast({
        title: t`Content generation started`,
        description: t`The learner guide and chat lessons are being written onto this unit.`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      await utils.unitsOfCompetency.continuableGenerationByUnitId.invalidate();
      onClose();
      navigate('/units/generation');
    },
    onError: () => utils.unitAssessment.getDraftCoverage.invalidate({ runId }),
  });

  const title = <Trans>Review coverage</Trans>;

  if (coverage == null) {
    return (
      <ReviewShell isOpen onClose={onClose} title={title}>
        <PendingBody error={error} />
      </ReviewShell>
    );
  }

  const { unitCode, hasBlockers } = coverage;

  return (
    <ReviewShell
      isOpen
      fitContent
      onClose={onClose}
      title={title}
      subtitle={<Trans>Coverage of {unitCode} requirements by the assessment questions.</Trans>}
    >
      <ModalBody display="flex" flexDirection="column" overflow="hidden" gap={4}>
        {continueMutation.error == null ? null : (
          <ErrorAlert message={continueMutation.error.message} />
        )}

        <CoverageReviewTable requirements={coverage.requirements} questions={coverage.questions} />
      </ModalBody>

      <ModalFooter gap={2}>
        {hasBlockers ? (
          <BlockersAlert
            status="error"
            messages={[
              t`Requirement mapping contains errors. Hover a highlighted question number or requirement for detail.`,
            ]}
            data-testid={testIds.coverageBlockers}
          />
        ) : (
          <Alert
            status="success"
            borderRadius="md"
            w="auto"
            py={1.5}
            mr="auto"
            data-testid={testIds.coverageClean}
          >
            <AlertIcon />
            <Text fontSize="sm">
              <Trans>
                Every requirement is covered, and every question is complete and linked.
              </Trans>
            </Text>
          </Alert>
        )}

        <Button variant="secondary" onClick={onClose}>
          <Trans>Close</Trans>
        </Button>

        <Button
          colorScheme="brand"
          isDisabled={hasBlockers}
          isLoading={continueMutation.isPending}
          data-testid={testIds.continueGenerationButton}
          onClick={() => continueMutation.mutate({ id: runId })}
        >
          {isRetry ? <Trans>Retry content generation</Trans> : <Trans>Continue generation</Trans>}
        </Button>
      </ModalFooter>
    </ReviewShell>
  );
}
